///
/// EarthBrowser
///
/// Migration v20 to v21: Add dock mode fields to persisted window states
/// Created by Matt Giger on December 28, 2025
/// Copyright (c) 2025 Geodata Labs
///

interface PersistedWindowStateV20 {
	dockMode?: string
	isDockedInDrawer?: boolean
}

interface BottomDrawerV20 {
	height?: number
	isOpen?: boolean
	dockedWindowIds?: string[]
}

interface UserInterfaceV20 {
	persistedWindowStates?: Record<string, PersistedWindowStateV20>
	bottomDrawer?: BottomDrawerV20
}

interface StateV20 {
	userInterface?: UserInterfaceV20
}

/**
 * Migration: v20 to v21
 *
 * Changes:
 * - Adds dockMode field to PersistedWindowState (default: 'both')
 * - Adds isDockedInDrawer field to PersistedWindowState (default: false)
 * - Adds bottomDrawer field to userInterface for persisted drawer state
 *
 * These fields support the new bottom drawer docking feature.
 */
export function migrateV20toV21(state: unknown): unknown {
	if (!state) return state

	// Clone the state to avoid mutations
	const newState = { ...(state as StateV20) }

	// Update userInterface slice
	if (newState.userInterface) {
		// Update persisted window states
		if (newState.userInterface.persistedWindowStates) {
			const updatedWindowStates: Record<string, PersistedWindowStateV20> = {}

			for (const [windowId, windowState] of Object.entries(newState.userInterface.persistedWindowStates)) {
				updatedWindowStates[windowId] = {
					...windowState,
					// Add new fields with defaults if not present
					dockMode: windowState.dockMode ?? 'both',
					isDockedInDrawer: windowState.isDockedInDrawer ?? false,
				}
			}

			newState.userInterface = {
				...newState.userInterface,
				persistedWindowStates: updatedWindowStates,
			}
		}

		// Add bottomDrawer field if not present
		if (!newState.userInterface.bottomDrawer) {
			newState.userInterface = {
				...newState.userInterface,
				bottomDrawer: {
					height: 400,
					isOpen: true,
					dockedWindowIds: [],
				},
			}
		} else if (newState.userInterface.bottomDrawer.isOpen === undefined) {
			// Add isOpen if missing from existing bottomDrawer
			newState.userInterface = {
				...newState.userInterface,
				bottomDrawer: {
					...newState.userInterface.bottomDrawer,
					isOpen: true,
				},
			}
		}
	}

	return newState
}
